import { OktaAuth } from '@okta/okta-auth-js';
export const PENDING_REASON_OKTA_HANDLE_REDIRECT = 'oktaHandleRedirect';
export const PENDING_REASON_OKTA_SIGNIN_REDIRECT = 'oktaSigninRedirect';
/**
 * Represents the okta setup.
 *
 * This encapsulates the logic and communication with Okta including the basic
 * login flow and redirects.
 */
class Okta {
    /** The okta client. */
    client;
    /**
     * Logs into okta and returns the tokens.
     *
     * This triggers multiple redirects throughout the process before returning
     * the tokens to the caller.
     *
     * @param options The login options.
     */
    async logIn({ client_id: clientId, issuer_url: issuer, }) {
        this.client = new OktaAuth({
            redirectUri: `${window.location.origin}/dashboard?oktacallback`,
            pkce: false,
            issuer,
            clientId,
        });
        const isLoginRedirect = this.client.isLoginRedirect();
        const isAuthenticated = await this.client.isAuthenticated();
        if (isLoginRedirect) {
            // The await here is crucial. If we don't await, the auth flow will continue and race to eventually do a No-Token-Redirect if the function is allowed to return.
            await this.client.handleLoginRedirect();
            // We should never get to the return, but if we do, we need to return a promise. Because typescript.
            // Okta should redirect, effectively killing/restarting the app.
            // If we do return asyncStatus: "pending", the auth init will continue without error,
            // but the AuthManager will not get initialized with working tokens, shortly after a request will fail and cause a No-Token-Redirect.
            return {
                asyncStatus: 'pending',
                pendingReason: PENDING_REASON_OKTA_HANDLE_REDIRECT,
            };
        }
        if (!isAuthenticated) {
            // The await here is crucial. If we don't await, the auth flow will continue and race to eventually do a No-Token-Redirect if the function is allowed to return.
            await this.client.signInWithRedirect();
            // We should never get to the return, but if we do, we need to return a promise. Because typescript.
            // Okta should redirect, effectively killing the app.
            // If we do return asyncStatus: "pending", the auth init will continue without error,
            // but the AuthManager will not get initialized with working tokens, shortly after a request will fail and cause a No-Token-Redirect.
            return {
                asyncStatus: 'pending',
                pendingReason: PENDING_REASON_OKTA_SIGNIN_REDIRECT,
            };
        }
        const tokens = await this.client.tokenManager.getTokens();
        return { asyncStatus: 'fulfilled', tokens };
    }
    /** Logs out of okta. */
    logOut() {
        this.clearTokens();
        this.redirectToIssuer();
    }
    /** Clears the okta tokens. */
    clearTokens() {
        if (this.client) {
            this.client.tokenManager.clear();
        }
    }
    /** Redirects to the issuer. */
    redirectToIssuer() {
        if (this.client?.options?.issuer) {
            window.location.href = this.client.options.issuer;
        }
    }
}
export const okta = new Okta();
//# sourceMappingURL=okta.js.map